import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Mail } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { CONTACT, ORG_NAME } from "./legal/LegalLayout";

/**
 * Public contact / branches page. Reuses the single CONTACT source of truth so
 * branch details never drift between here and the legal pages.
 */
export default function ContactPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 sticky top-0 bg-background/90 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Home">
            <BrandLogo size="md" />
          </Link>
          <Link
            to="/"
            className="text-xs font-semibold text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="size-4" />
            Back to Home / হোমে ফিরুন
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="mb-8 border-b border-border/60 pb-6">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Contact &amp; Branches</h1>
          <p className="text-lg text-muted-foreground font-medium mt-1">যোগাযোগ ও শাখাসমূহ</p>
          <p className="text-sm text-muted-foreground mt-3">{ORG_NAME}</p>
        </div>

        {/* Emails */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="border border-border rounded-2xl bg-muted/30 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Mail className="size-4 text-primary" /> General Enquiries
            </div>
            <a href={`mailto:${CONTACT.email}`} className="text-sm font-mono text-primary hover:underline mt-1 inline-block">
              {CONTACT.email}
            </a>
          </div>
          <div className="border border-border rounded-2xl bg-muted/30 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Mail className="size-4 text-primary" /> Support &amp; Refunds
            </div>
            <a href={`mailto:${CONTACT.supportEmail}`} className="text-sm font-mono text-primary hover:underline mt-1 inline-block">
              {CONTACT.supportEmail}
            </a>
          </div>
        </div>

        {/* Branches */}
        <h2 className="text-base font-bold mb-3">Our Branches / আমাদের শাখাসমূহ</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CONTACT.branches.map((b) => (
            <div key={b.name} className="border border-border rounded-2xl bg-background p-5 space-y-2">
              <div className="font-bold text-sm">
                {b.name} <span className="text-muted-foreground font-normal">/ {b.nameBn}</span>
              </div>
              <a
                href={`tel:${b.phoneTel}`}
                className="flex items-center gap-2 text-sm font-mono text-primary hover:underline"
              >
                <Phone className="size-4 shrink-0" />
                {b.phone}
              </a>
              <div className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                <MapPin className="size-4 shrink-0 mt-0.5 text-primary" />
                <span>{b.address}</span>
              </div>
            </div>
          ))}
        </div>

        <nav className="mt-10 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-primary transition-colors">Terms &amp; Conditions</Link>
          <Link to="/refund-policy" className="hover:text-primary transition-colors">Refund Policy</Link>
          <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookie Policy</Link>
        </nav>

        <p className="mt-8 text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} {ORG_NAME}. All rights reserved. / সর্বস্বত্ব সংরক্ষিত।
        </p>
      </main>
    </div>
  );
}
